/* React */
import { Component, createRef } from "react"
/* ReactDOM portal */
import ReactDOM from "react-dom"
/* Redux connect */
import { connect } from "react-redux"

/* Icon component */
import Icon from "./Icon"
/* Helpers */
import { utils } from "../helpers"
/* Organization API */
import { get, services as orgServices, specialists as orgSpecialists, availability } from "../api/Organization"
/* Booking API */
import { create } from "../api/Booking"
/* Translation */
import { translate } from "../translates"

const DOW = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"]
const MONTHS = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]

/* Component BookingModal */
class BookingModal extends Component {

    constructor(props) {
        super(props)
        /* State */
        this.state = {
            loading: true,
            error: false,
            organization: null,
            services: [],
            specialists: [],
            slots: [],
            /* The master of the visit — the client picks a named one, there is no «any» */
            specialist: null,
            /* Which of the dates ahead have an hour to offer at all. A day with none of them is
               not shown: an empty «выберите время» tells the client nothing */
            days: [],
            dateIdx: 0,
            time: null,
            submitting: false,
            success: false,
            submitError: null,
            /* «Записаться» was pressed before an hour was picked — the hours are lit up */
            timeMissing: false
        }

        /* The hours of the date — the button brings the client to them when none is picked */
        this._times = createRef()
    }

    /* Turn a booking response into a human-readable Russian error, or null when it succeeded */
    _submitError = (response) => {
        if (!response) {
            return "Нет соединения с сервером. Проверьте интернет и попробуйте ещё раз."
        }
        if (response.status === 201 || response.status === 200) {
            return null
        }
        if (response.status === 409 && response.data && response.data.error && response.data.error.code === "SERVICE_NOT_PROVIDED") {
            return "Этот мастер не оказывает выбранную услугу. Выберите другого."
        }
        if (response.status === 409) {
            return "Это время только что заняли. Выберите другое."
        }
        if (response.status === 401 || response.status === 498) {
            /* Handled in _submit, which opens the login modal and sets its own message */
            return null
        }
        if (response.status === 400) {
            return "Проверьте выбранные услугу, дату и время."
        }
        return "Не удалось создать запись. Попробуйте позже."
    }

    componentDidMount = () => {
        document.addEventListener("keydown", this._onKey)
        document.body.style.overflow = "hidden"
        this._load()
    }

    componentWillUnmount = () => {
        document.removeEventListener("keydown", this._onKey)
        document.body.style.overflow = ""
    }

    /* Draw On Key */
    _onKey = (event) => {
        if (event.key === "Escape") {
            this.props.onClose()
        }
    }

    /* Draw Dates */
    _dates = () => {
        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date()
            date.setDate(date.getDate() + i)
            /* Format in local time — toISOString() would shift to UTC and roll the date back in Almaty */
            const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
            return {
                iso,
                wd: date.getDay(),
                dow: DOW[date.getDay()],
                day: date.getDate(),
                mon: MONTHS[date.getMonth()]
            }
        })
    }

    /* Draw Off Days */
    /* Weekday indices (JS getDay) the organization is closed, from its schedule */
    _offDays = (org) => {
        const source = org || this.state.organization
        const schedule = source && source.hours ? source.hours.schedule : []
        const MAP = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 }
        const off = new Set()
        ;(schedule || []).forEach((entry) => { if (entry.dayOff) off.add(MAP[entry.day]) })
        return off
    }

    /* Draw Load */
    _load = () => {
        /* Fields */
        const { repeat } = this.props
        this.setState({ loading: true, error: false })

        Promise.all([
            get(repeat.organizationId),
            orgServices(repeat.organizationId),
            orgSpecialists(repeat.organizationId, repeat.serviceId ? [repeat.serviceId] : [])
        ]).then(([resOrg, resServices, resSpec]) => {
            if (resOrg && resOrg.status === 200) {
                /* Services API returns either a flat array (legacy) or { serviceType, services, groups } */
                const serviceData = resServices && resServices.status === 200 ? resServices.data : null
                const allServices = serviceData ? (Array.isArray(serviceData) ? serviceData : (serviceData.services || [])) : []
                const specs = resSpec && resSpec.status === 200 ? resSpec.data : []

                let selectedServices = []
                if (repeat.serviceId) {
                    selectedServices = allServices.filter((svc) => `${svc.id}` === `${repeat.serviceId}`)
                }
                if (!selectedServices.length && repeat.service) {
                    selectedServices = allServices.filter((svc) => svc.name === repeat.service)
                }
                if (!selectedServices.length && allServices.length) {
                    selectedServices = [allServices[0]]
                }

                /* Start on the first working day, skipping the organization's days off */
                const off = this._offDays(resOrg.data)
                const firstWorking = this._dates().findIndex((date) => !off.has(date.wd))

                this.setState({
                    organization: resOrg.data,
                    services: selectedServices,
                    specialists: specs,
                    specialist: specs.length > 0 ? specs[0].id : null,
                    dateIdx: firstWorking >= 0 ? firstWorking : 0,
                    loading: false
                }, this._loadSlots)
            }
            else {
                this.setState({ loading: false, error: true })
            }
        })
    }

    /* Draw Load Slots — the hours of the chosen date, and which of the dates ahead have any
       hours at all. Both come in one answer, so the strip of the days never asks separately */
    _loadSlots = () => {
        /* Fields */
        const { organization, services, specialist, dateIdx } = this.state
        if (!organization || organization.queue) {
            return
        }
        const dates = this._dates()
        const date = dates[dateIdx].iso
        const serviceIds = services.map((svc) => svc.id).join(",")
        availability(organization.id, { serviceIds, specialistId: specialist, date, days: dates.length }).then((response) => {
            if (response && response.status === 200) {
                const days = Array.isArray(response.data.days) ? response.data.days : []
                this.setState({ slots: response.data.slots || [], days, time: null }, this._keepDay)
            }
        })
    }

    /* Draw Keep Day — the date which is open must be one of those the strip still shows. A
       master who does not work the day the form opened at moves the client to his nearest one */
    _keepDay = () => {
        /* Fields */
        const { days, dateIdx } = this.state
        const dates = this._dates()

        /* The answer said nothing about the days — the strip stays as it is */
        if (days.length === 0) {
            return
        }

        const open = (iso) => days.some((item) => item.date === iso && item.available)

        /* The date which is open is still worth standing on */
        if (dates[dateIdx] && open(dates[dateIdx].iso)) {
            return
        }

        const next = dates.findIndex((item) => open(item.iso))

        /* Nowhere to move to — the master has no hours in the whole week */
        if (next === -1 || next === dateIdx) {
            return
        }

        this.setState({ dateIdx: next }, this._loadSlots)
    }

    /* Draw No Dates — the master has no free hour in the whole week ahead. The advice depends
       on whether there is anybody else to go to: a point with one master can only be waited for */
    _noDates = () => {
        /* Fields */
        const { specialists } = this.state

        return (
            <div className="slots-empty">

                <div className="slots-empty__art">
                    <Icon name="calendar" size={20} />
                </div>

                <div className="slots-empty__body">

                    <div className="slots-empty__title">{translate("Свободного времени нет")}</div>

                    <div className="slots-empty__text">
                        {specialists.length > 1
                            ? translate("На ближайшую неделю мастер занят. Выберите другого специалиста — у него может быть свободнее")
                            : translate("На ближайшую неделю мастер занят. Загляните позже или позвоните в заведение")
                        }
                    </div>

                </div>

            </div>
        )
    }

    /* Draw Pick Date — the strip shows a part of the week, so the date is found by itself */
    _pickDate = (date) => {
        /* Fields */
        const index = this._dates().findIndex((item) => item.iso === date.iso)

        if (index === -1) {
            return
        }

        this.setState({ dateIdx: index }, this._loadSlots)
    }

    /* Draw Open Dates — the dates the strip shows: the ones the master really has hours on.
       Until the first answer arrives the days off of the point are the best guess there is */
    _openDates = () => {
        /* Fields */
        const { days } = this.state
        const dates = this._dates()

        if (days.length === 0) {
            const off = this._offDays()
            return dates.filter((item) => !off.has(item.wd))
        }

        return dates.filter((item) => days.some((day) => day.date === item.iso && day.available))
    }

    /* Draw Ask Time — the hour is not picked: the hours are brought into view and lit up.
       On a phone they stand far above the button, and a button which silently does nothing
       reads as a broken one */
    _askTime = () => {
        this.setState({ timeMissing: true }, () => {
            if (this._times.current) {
                this._times.current.scrollIntoView({ behavior: "smooth", block: "center" })
            }
        })
    }

    /* Draw Submit */
    _submit = () => {
        /* Fields */
        const { organization, services, specialist, dateIdx, time } = this.state

        /* Check */
        if (time === null) {
            this._askTime()
            return
        }

        this.setState({ submitting: true, submitError: null })

        create(this.props.token, {
            organizationId: organization.id,
            serviceIds: services.map((svc) => svc.id),
            specialistId: specialist,
            date: this._dates()[dateIdx].iso,
            time
        }).then((response) => {
            /* Session expired mid-booking — ask to log in again in place. The banner stays behind the
               login modal so the reason is still on screen if the client closes it: without it the
               button just resets and the booking looks like it failed for no reason. */
            if (response && (response.status === 401 || response.status === 498)) {
                this.setState({ submitting: false, submitError: "Сессия истекла. Войдите ещё раз и нажмите «Записаться»." })
                this.props.toggleAuth(true)
                return
            }
            const submitError = this._submitError(response)
            this.setState({ submitting: false, submitError })
            if (!submitError) {
                this.setState({ success: true })
                return
            }
            /* A taken slot is stale data — refresh the times so the user can pick another */
            if (response && response.status === 409) {
                this._loadSlots()
            }
        })
    }

    /* Draw Section Label */
    _sectionLabel = (text) => (
        <h4 style={{ fontSize: 14, fontWeight: 800, margin: "0 0 12px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--muted)" }}>
            {text}
        </h4>
    )

    /* Draw Content */
    _content = () => {
        /* Fields */
        const { loading, error, success, organization, services, specialists, slots, specialist, dateIdx, time, submitting, timeMissing } = this.state

        /* The date which is open, by itself and not by its place in the week */
        const week = this._dates()
        const selected = week[dateIdx] ? week[dateIdx].iso : ""
        const { onClose } = this.props
        /* Only the dates the master really has hours on stand in the strip */
        const dates = this._openDates()

        if (loading) {
            return (
                <div className="modal__body" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid var(--line)", borderTopColor: "var(--accent)", animation: "bkspin 0.7s linear infinite" }} />
                </div>
            )
        }

        if (error || !organization) {
            return (
                <div className="modal__body" style={{ textAlign: "center", padding: "48px 28px" }}>
                    <div style={{ color: "var(--danger)", marginBottom: 12 }}><Icon name="calendar" size={36} /></div>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>{translate("Не удалось загрузить")}</div>
                    <button className="btn btn--ghost" style={{ padding: "10px 20px" }} onClick={this._load}>{translate("Повторить")}</button>
                </div>
            )
        }

        if (success) {
            return (
                <>
                    <div className="modal__body" style={{ textAlign: "center", padding: "48px 28px" }}>
                        <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--success)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                            <Icon name="check" size={36} />
                        </div>
                        <h3 style={{ fontSize: 22, fontWeight: 800, margin: "0 0 8px" }}>{translate("Запись создана!")}</h3>
                        <p style={{ color: "var(--muted)", margin: "0 auto 28px", maxWidth: 340 }}>
                            {translate("Ждём вас в")} {organization.name}. {translate("Напомним за час до записи.")}
                        </p>
                        <button className="btn btn--lg btn--block" onClick={onClose}>{translate("Отлично")}</button>
                    </div>
                </>
            )
        }

        const total = services.reduce((sum, svc) => sum + utils.effectivePrice(svc), 0)
        const ready = time !== null

        return (
            <>
                <div className="modal__body">
                    <h2 style={{ fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>{organization.name}</h2>
                    <div style={{ display: "flex", gap: 12, color: "var(--muted)", fontSize: 13, marginTop: 6, marginBottom: 20 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Icon name="pin" size={14} /> {organization.addr}
                        </span>
                    </div>

                    <div style={{ padding: "14px 16px", background: "var(--bg-soft)", borderRadius: 12, marginBottom: 28 }}>
                        {services.map((svc, i) => (
                            <div key={`${svc.id}`} style={{ display: "flex", justifyContent: "space-between", padding: i > 0 ? "8px 0 0" : 0 }}>
                                <div>
                                    <div style={{ fontWeight: 700 }}>{svc.name}</div>
                                    <div style={{ color: "var(--muted)", fontSize: 13 }}>{svc.duration}</div>
                                </div>
                                <div style={{ fontWeight: 800 }}>
                                    {utils.price(svc)}
                                    {utils.hasDiscount(svc) && <span style={{ textDecoration: "line-through", color: "var(--muted)", fontWeight: 400, marginLeft: 6 }}>{utils.money(svc.price)}</span>}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginBottom: 28 }}>
                        {this._sectionLabel(translate("Выберите специалиста"))}
                        <div className="spec-grid">
                            {specialists.map((sp) => (
                                <button
                                    key={`${sp.id}`}
                                    className={`spec${specialist === sp.id ? " is-selected" : ""}`}
                                    onClick={() => this.setState({ specialist: sp.id, dateIdx: 0, time: null }, this._loadSlots)}
                                >
                                    <span className="spec__check"><Icon name="check" size={14} /></span>
                                    <div className="spec__avatar">
                                        {sp.avatar ? <img src={utils.media(sp.avatar)} alt="" onError={utils.onAvatarError} /> : <Icon name="user" size={36} />}
                                    </div>
                                    <div className="spec__name">{sp.name}</div>
                                    {sp.profession && <div className="spec__profession">{sp.profession}</div>}
                                    {sp.rating > 0 && (
                                        <div className="spec__rating">
                                            <Icon name="star" size={12} fill="currentColor" stroke="none" />
                                            {Number(sp.rating).toFixed(2)}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ marginBottom: 28 }}>
                        {this._sectionLabel(translate("Выберите дату"))}
                        {dates.length === 0 ? this._noDates() : (
                            <div className="date-strip">
                                {dates.map((date) => (
                                    <button
                                        key={date.iso}
                                        className={`date${selected === date.iso ? " is-selected" : ""}`}
                                        onClick={() => this._pickDate(date)}
                                    >
                                        <div className="date__dow">{translate(date.dow)}</div>
                                        <div className="date__day">{date.day}</div>
                                        <div className="date__mon">{translate(date.mon)}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* A week without a single free hour says so once, above — a second empty
                        block under it would only repeat the same thing */}
                    {dates.length > 0 &&
                    <div ref={this._times} className={timeMissing ? "times-block is-missing" : "times-block"}>
                        {this._sectionLabel(translate("Выберите время"))}
                        {timeMissing && <div className="times-hint">{translate("Выберите время, чтобы записаться")}</div>}
                        <div className="times">
                            {slots.map((slot) => (
                                <button
                                    key={slot.time}
                                    className={`time${time === slot.time ? " is-selected" : ""}`}
                                    disabled={!slot.available}
                                    onClick={() => this.setState({ time: slot.time, timeMissing: false })}
                                >
                                    {slot.time}
                                </button>
                            ))}
                            {slots.length === 0 && (
                                <div style={{ color: "var(--muted)", fontSize: 14, padding: "12px 0" }}>
                                    {translate("Нет доступных слотов на выбранную дату")}
                                </div>
                            )}
                        </div>
                    </div>
                    }
                </div>

                {this.state.submitError && (
                    <div style={{ padding: "12px 16px", margin: "0 20px", background: "var(--danger-bg)", color: "var(--danger)", borderRadius: 12, fontSize: 14, fontWeight: 600 }}>
                        {this.state.submitError}
                    </div>
                )}
                <div className="modal__footer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                    <div>
                        <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{translate("Итого")}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em" }}>{translate("от")} {utils.money(total)}</div>
                    </div>
                    {/* Without an hour the button names the next step and leads down to the
                        hours — a pale «Записаться» told the client nothing, and nobody guessed
                        the hours stood further down the window */}
                    <button
                        className="btn btn--lg"
                        style={{ padding: "14px 36px", opacity: submitting ? 0.5 : 1 }}
                        disabled={submitting}
                        onClick={this._submit}
                    >
                        {submitting ? translate("Записываем...") : (ready ? translate("Записаться") : translate("Выбрать время"))}
                        {!submitting && <Icon name={ready ? "arrow_right" : "chevron_down"} size={16} />}
                    </button>
                </div>
            </>
        )
    }

    render = () => {
        /* Fields */
        const { onClose } = this.props
        const { success } = this.state

        return ReactDOM.createPortal(
            <div className="modal-backdrop" onClick={success ? undefined : onClose}>
                <div className="modal modal--lg" onClick={(event) => event.stopPropagation()}>
                    <div className="modal__header">
                        <h3 className="modal__title">{translate("Новая запись")}</h3>
                        <button className="modal__close" onClick={onClose} aria-label={translate("Закрыть")}>
                            <Icon name="close" size={20} />
                        </button>
                    </div>
                    {this._content()}
                </div>
            </div>,
            document.body
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(BookingModal)
